import { useEffect, useState } from "react";

// Mirrors core.LandingFeedResponse (GET /v1/landing/feed). American odds
// arrive pre-formatted; the browser never derives them from share_bps.
export type LandingOutcome = {
  username: string;
  producer_id?: string;
  logo_url?: string;
  share_bps: number;
  staked_cents?: number;
  american_odds: string;
  backer_count: number;
  track_preview_url?: string;
};

export type LandingLiveContest = {
  slug: string;
  name: string;
  genre?: string;
  cover_url?: string;
  ends_at: string;
  pool_cents: number;
  take_bps?: number;
  // The take split into the platform fee, the winning producer's cut, and
  // the backing share every backed producer earns.
  rake_bps?: number;
  producer_bps?: number;
  backing_bps?: number;
  // The market's tier. Absent when the contest has no market.
  tier?: string;
  // The rake-free house bonus inside pool_cents.
  seed_cents?: number;
  // Bounds on one fan's stake, so the calculator never suggests a refused one.
  min_stake_cents?: number;
  per_user_cap_cents?: number;
  is_seeded: boolean;
  outcomes: LandingOutcome[];
};

export type LandingWin = {
  producer_username: string;
  contest_name: string;
  contest_slug?: string;
  placement?: number;
  payout_cents?: number;
  at: string;
};

// What moving money costs, as the server states it. BeatFlipz pays these;
// nothing in the browser recomputes a rate.
export type FeeSchedule = {
  card_bps: number;
  card_fixed_cents: number;
  payout_bps: number;
  payout_cap_cents: number;
  worst_case_bps: number;
};

export type LandingFeed = {
  paidOutThisWeekCents: number;
  liveContests: LandingLiveContest[];
  recentWins: LandingWin[];
  winnersWall: LandingWin[];
  fees: FeeSchedule | null;
};

// Below this weekly total the hero shows no counter at all: no counter
// beats a near-zero one.
export const PAYOUT_COUNTER_FLOOR_CENTS = 10_000;

const FEED_URL = "/v1/landing/feed";

const list = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

// Keep only the fields the page uses, so nothing extra in a payload (an email,
// say) can reach the DOM.
function pickWin(w: LandingWin): LandingWin {
  return {
    producer_username: w.producer_username,
    contest_name: w.contest_name,
    contest_slug: w.contest_slug,
    placement: w.placement,
    payout_cents: w.payout_cents,
    at: w.at,
  };
}

const FEE_FIELDS = ["card_bps", "card_fixed_cents", "payout_bps", "payout_cap_cents", "worst_case_bps"] as const;

// pickFees keeps the schedule only when the server sent every rate as a
// number of zero or more. A half-sent one is no schedule at all.
function pickFees(raw: unknown): FeeSchedule | null {
  if (!raw || typeof raw !== "object") return null;
  const f = raw as Record<string, unknown>;
  const ok = FEE_FIELDS.every((k) => typeof f[k] === "number" && Number.isFinite(f[k] as number) && (f[k] as number) >= 0);
  if (!ok) return null;
  return {
    card_bps: f.card_bps as number,
    card_fixed_cents: f.card_fixed_cents as number,
    payout_bps: f.payout_bps as number,
    payout_cap_cents: f.payout_cap_cents as number,
    worst_case_bps: f.worst_case_bps as number,
  };
}

export function normalizeFeed(raw: unknown): LandingFeed {
  const body = (raw ?? {}) as Record<string, unknown>;
  return {
    paidOutThisWeekCents: Number(body.paid_out_this_week_cents) || 0,
    liveContests: list<LandingLiveContest>(body.live_contests).map((c) => ({
      ...c,
      outcomes: list<LandingOutcome>(c.outcomes),
    })),
    recentWins: list<LandingWin>(body.recent_wins).map(pickWin),
    winnersWall: list<LandingWin>(body.winners_wall).map(pickWin),
    fees: pickFees(body.fees),
  };
}

// useLandingFeed returns null until the feed resolves, and stays null if it
// fails. Callers render static sections regardless and drop data sections.
export function useLandingFeed(): LandingFeed | null {
  const [feed, setFeed] = useState<LandingFeed | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(FEED_URL, { signal: ctrl.signal, headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`feed ${res.status}`))))
      .then((raw) => setFeed(normalizeFeed(raw)))
      .catch(() => {
        // A failed feed is not an error page: the static pitch still stands.
      });
    return () => ctrl.abort();
  }, []);
  return feed;
}

// A pick is one producer line in one contest: what a card shows and what
// the slip carries.
export type Pick = { contest: LandingLiveContest; outcome: LandingOutcome };

export function pickKey({ contest, outcome }: Pick): string {
  return `${contest.slug}:${outcome.producer_id ?? outcome.username}`;
}

export function contestHref(slug: string, producerId?: string): string {
  const path = `/contest/${encodeURIComponent(slug)}`;
  return producerId ? `${path}?pick=${encodeURIComponent(producerId)}` : path;
}

// A Back Me link: the contest with this producer ready in the bet slip.
// Usernames, not ids, so the link is readable and carries no id.
export function backMeHref(slug: string, username: string): string {
  return `/contest/${encodeURIComponent(slug)}?back=${encodeURIComponent(username)}`;
}

// A producer's public page.
export function producerHref(username: string): string {
  return `/p/${encodeURIComponent(username)}`;
}
